// Reference resolution for gt, eq and authored templates (E01-S2-T3, decided in D-04).
//
// A reference is a string beginning with `$`; anything else is a literal. The allow-list below is
// the whole of what a reference may address. Resolution never walks an arbitrary path: the
// reference is matched against the known patterns and exactly one slot is read, with own-property
// checks, so `__proto__`, `constructor` and array indexes resolve to nothing.
//
// This file must not import zod: it ships in the content script with the evaluator.
export const COLLECTION_PROPS = [
    "rowCount",
    "selectedCount",
    "pageSize",
    "page",
    "pages",
    "total",
];
export const FIELD_PROPS = ["value", "present", "length", "bandIndex"];
// Ids inside a reference are field and collection ids: a letter first, no dots (a dot separates segments).
const ID = "[A-Za-z][A-Za-z0-9_-]{0,63}";
const KEY = "[A-Za-z][A-Za-z0-9_-]{0,63}";
const CONTEXT_RE = new RegExp(`^screen\\.context\\.(${KEY})$`);
const COLLECTION_RE = new RegExp(`^collections\\.(${ID})\\.(${COLLECTION_PROPS.join("|")})$`);
const FIELD_RE = new RegExp(`^fields\\.(${ID})\\.(${FIELD_PROPS.join("|")})$`);
/** Every allow-listed reference pattern, without the leading `$`. Templates reuse the same list. */
export const REF_PATTERNS = [
    { id: "screen.id", re: /^screen\.id$/, example: "screen.id" },
    { id: "screen.context.<key>", re: CONTEXT_RE, example: "screen.context.queue" },
    { id: "focus.target", re: /^focus\.target$/, example: "focus.target" },
    { id: "overlays.count", re: /^overlays\.count$/, example: "overlays.count" },
    { id: "collections.<id>.<prop>", re: COLLECTION_RE, example: "collections.cases.total" },
    { id: "fields.<id>.<prop>", re: FIELD_RE, example: "fields.houseCallAmt.present" },
];
export const isRef = (v) => typeof v === "string" && v.startsWith("$");
const FORBIDDEN = new Set(["__proto__", "constructor", "prototype"]);
/** Parse a path (with or without the leading `$`) against the allow-list. Null when not allowed. */
export function parseRef(ref) {
    if (typeof ref !== "string")
        return null;
    const path = ref.startsWith("$") ? ref.slice(1) : ref;
    if (path.length > 300)
        return null;
    let m;
    if (/^screen\.id$/.test(path))
        return { root: "screen.id" };
    // A match always has its groups; a missing one is treated like any reference not allowed.
    if ((m = CONTEXT_RE.exec(path))) {
        const key = m[1];
        return key === undefined || FORBIDDEN.has(key) ? null : { root: "screen.context", key };
    }
    if (/^focus\.target$/.test(path))
        return { root: "focus.target" };
    if (/^overlays\.count$/.test(path))
        return { root: "overlays.count" };
    if ((m = COLLECTION_RE.exec(path))) {
        const [, id, prop] = m;
        return id === undefined || prop === undefined || FORBIDDEN.has(id)
            ? null
            : { root: "collections", id, prop: prop };
    }
    if ((m = FIELD_RE.exec(path))) {
        const [, id, prop] = m;
        return id === undefined || prop === undefined || FORBIDDEN.has(id)
            ? null
            : { root: "fields", id, prop: prop };
    }
    return null;
}
const isPlain = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
const own = (o, k) => isPlain(o) && !FORBIDDEN.has(k) && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined;
const num = (v) => typeof v === "number" && Number.isFinite(v) ? v : undefined;
const str = (v) => (typeof v === "string" ? v : undefined);
/**
 * Resolve one reference against a frame. Returns undefined for anything missing, not allowed, or
 * of the wrong type. Never throws, whatever the frame is.
 */
export function resolveRef(ref, frame) {
    try {
        return resolve(ref, frame);
    }
    catch {
        return undefined; // a hostile getter or proxy on the frame
    }
}
function resolve(ref, frame) {
    const p = parseRef(ref);
    if (!p || !isPlain(frame))
        return undefined;
    switch (p.root) {
        case "screen.id":
            return str(own(own(frame, "screen"), "id"));
        case "screen.context":
            return str(own(own(own(frame, "screen"), "context"), p.key));
        case "focus.target":
            return str(own(own(frame, "focus"), "target"));
        case "overlays.count": {
            const o = own(frame, "overlays");
            return Array.isArray(o) ? o.length : undefined;
        }
        case "collections":
            return num(own(own(own(frame, "collections"), p.id), p.prop));
        case "fields": {
            const f = own(own(frame, "fields"), p.id);
            if (!isPlain(f))
                return undefined;
            const cls = own(f, "cls");
            switch (p.prop) {
                case "value":
                    return cls === "structural" || cls === "enum" ? str(own(f, "value")) : undefined;
                case "present": {
                    if (cls === "numeric" || cls === "free-text") {
                        const v = own(f, "present");
                        return typeof v === "boolean" ? v : undefined;
                    }
                    return cls === "structural" || cls === "enum" || cls === "identifier" || cls === "pii"
                        ? true
                        : undefined; // the key exists: the same answer as { field }
                }
                case "length":
                    return cls === "free-text" ? num(own(f, "length")) : undefined;
                case "bandIndex":
                    return cls === "numeric" ? num(own(f, "bandIndex")) : undefined;
                default:
                    return undefined;
            }
        }
        default:
            return undefined;
    }
}
//# sourceMappingURL=refs.js.map